import type { Plan } from "@cpos/shared/entitlements";

// ── Domain types ──────────────────────────────────────────────────────────────

export type OverallStage = "onboarding" | "active" | "suspended" | "churned";

export type OnboardingStage =
  | "registered"
  | "details_required"
  | "verifying"
  | "input_required"
  | "setup_needed"
  | "ready"
  | "active";

export type IdentityStage =
  | "verified"
  | "duplicate"
  | "pending_backfill"
  | "failed"
  | "unrecoverable"
  | "not_verified";

export interface MerchantRow {
  id: string;
  businessName: string;
  plan: Plan | null;
  overallStage: OverallStage | null;
  onboardingStage: OnboardingStage | null;
  identityStage: IdentityStage | null;
  subscriptionStatus: string | null;
  /** ISO instant the business signed up; null when the source does not carry it. */
  createdAt: string | null;
  /** ISO instant of its earliest Pro conversion; null when it never converted. */
  proSince: string | null;
  updatedAt: number | null;
}

// ── Filters ───────────────────────────────────────────────────────────────────

export interface MerchantFilters {
  /** Server-side: DynamoDB `contains` filter on businessName. */
  searchQuery: string;
  /** Server-side: `eq` filter on subscriptionStatus (OR across multiple values). */
  subscriptionStatuses: string[];
  /** Client-side: applied to the current page of results. */
  overallStages: OverallStage[];
  /** Client-side: applied to the current page of results. */
  onboardingStages: OnboardingStage[];
  /** Client-side: applied to the current page of results. */
  identityStages: IdentityStage[];
  /** Plan the merchant is on now. Null means any. */
  plan: Plan | null;
  /** Epoch ms, inclusive. A half-open window `[from, to)` with `signedUpTo`. */
  signedUpFrom: number | null;
  /** Epoch ms, exclusive. */
  signedUpTo: number | null;
  /** Epoch ms, inclusive. A half-open window `[from, to)` with `proSinceTo`. */
  proSinceFrom: number | null;
  /** Epoch ms, exclusive. */
  proSinceTo: number | null;
}

export function defaultFilters(): MerchantFilters {
  return {
    searchQuery: "",
    subscriptionStatuses: [],
    overallStages: [],
    onboardingStages: [],
    identityStages: [],
    plan: null,
    signedUpFrom: null,
    signedUpTo: null,
    proSinceFrom: null,
    proSinceTo: null,
  };
}

/**
 * Filter keys that are applied client-side on the current page.
 * Patches touching only these keys do not require a server round-trip.
 */
const CLIENT_ONLY_FILTER_KEYS: (keyof MerchantFilters)[] = [
  "overallStages",
  "onboardingStages",
  "identityStages",
];

/**
 * Returns true when every key in the patch is a client-side-only filter key.
 * Used by the BLoC to skip a server fetch when only client filters changed.
 */
export function isClientOnlyPatch(patch: Partial<MerchantFilters>): boolean {
  const keys = Object.keys(patch) as (keyof MerchantFilters)[];
  return (
    keys.length > 0 && keys.every((k) => CLIENT_ONLY_FILTER_KEYS.includes(k))
  );
}

// ── Search ──────────────────────────────────────────────────────────────────

export type MerchantSearchType =
  "email" | "businessName" | "customerName" | "businessId";

export interface MerchantSearch {
  type: MerchantSearchType;
  query: string; // empty => browse mode
}

export function defaultSearch(): MerchantSearch {
  return { type: "businessName", query: "" };
}

/** Hard cap on scan pages for name searches before asking the admin to refine. */
export const MAX_SCAN_PAGES = 6; // 6 * 500 = 3,000 rows ceiling
export const SCAN_PAGE_LIMIT = 500;

// ── Selection set ─────────────────────────────────────────────────────────────

export const MERCHANT_SELECTION_SET = [
  "id",
  "businessName",
  "plan",
  "subscriptionStatus",
  "lifecycle.overallStage",
  "lifecycle.identityStage",
  "lifecycle.tracks",
  "lifecycle.updatedAt",
] as const;
